import React, {useState} from 'react';
import {styles} from './listItem_styles.web';
import {ListItemProps} from './listItem_types.web';
import {useTheme} from '../../../../utils/theming/themeProvider';
import Avatar from '../../Avatars/Avatar/Avatar.web';
import ListMetaContainer from '../ListMetaContainer/ListMetaContainer.web';
import MiniCredentialCard from '../../Cards/MiniCredential/miniCredential.web';
import Checkbox from '../../Checkbox/Checkbox/checkbox.web';
import RadioButton from '../../RadioButton/RadioButton/radioButton.web';
import Switch from '../../Switch/Switch/Switch.web';
import Indicator from '../../Indicator/Indicator/indicator.web';
import SegmentedButton from '../../Buttons/SegmentedButton/index.web';
import ActionItems from '../../Action/ActionItems/actionItems.web';
import {MoreVerticalIcon} from '../../Icons/index.web';
import Button from '../../Buttons/Button/Button.web';
import Chip from '../../Chips/Chip/Chip.web';
import IconGroup from '../../Icons/IconGroup/IconGroup.web';
import ButtonIcon from '../../Buttons/ButtonIcon/ButtonIcon.web';
import {cursorTo} from 'readline';
import ChipGroup from '../../Chips/ChipGroup/ChipGroup.web';

const ListItem = (props: React.PropsWithChildren<ListItemProps>) => {
  // Props
  const {
    id,
    className,
    type = 'filled',
    state = 'enable',
    condition = 'all',
    title,
    content,
    extraInfo,
    mainTextColor,
    mainTextInfoColor,
    leading,
    trailing,
    helperText,
    avatar,
    credential,
    leadingIcon,
    trailingIcon,
    checkbox,
    switchOpt,
    radioButton,
    segmentedButton,
    trailingValue,
    actionItem,
    notification,
    button,
    hasHoverStyle,
    mainActionItem,
    chip,
    chipGroup,
    leadingChip,
    leadingButtonIcon,
    iconGroup,
    onClick,
    containerStyle,
  } = props;

  // Styles
  const {colors, fontStyles} = useTheme();
  const [isHovered, setIsHovered] = useState(false);
  const generalStyles = styles();
  const shouldBeFullyClickable =
    onClick &&
    trailing !== 'switch' &&
    trailing !== 'button' &&
    trailing !== 'segmentedButton';

  return (
    <div
      id={id}
      className={className}
      style={{
        ...generalStyles.generalContainer,
        ...containerStyle,
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}>
      <div
        style={{
          ...generalStyles.listItemContainer,
          backgroundColor:
            isHovered && hasHoverStyle && state === 'enable'
              ? colors.neutral200
              : state === 'active'
              ? colors.primary700
              : type === 'filled'
              ? colors.neutral100
              : 'transparent',
          borderBottomWidth: type === 'outline' ? 1 : 0,
          borderLeftWidth: type === 'outline' ? 1 : 0,
          borderRightWidth: type === 'outline' ? 1 : 0,
          border: type === 'outline' ? `1px solid ${colors.neutral300}` : 0,
          borderTop:
            type === 'divider' || type === 'outline'
              ? `1px solid ${colors.neutral300}`
              : 0,

          borderTopLeftRadius: type === 'divider' ? 0 : 8,
          borderTopRightRadius: type === 'divider' ? 0 : 8,
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
          cursor: shouldBeFullyClickable ? 'pointer' : 'auto',
        }}
        onClick={shouldBeFullyClickable ? onClick : () => {}}>
        <div style={{...generalStyles.listItemLeftContainer}}>
          {leading === 'chip' && leadingChip ? <Chip {...leadingChip} /> : null}
          {leading === 'chipGroup' && chipGroup ? (
            <ChipGroup {...chipGroup} />
          ) : null}
          {leading === 'avatar' && avatar ? (
            <Avatar {...avatar} color={avatar?.color || 'black'} />
          ) : null}
          {leading === 'credential' && credential ? (
            <MiniCredentialCard
              {...credential}
              containerStyle={{
                cursor: shouldBeFullyClickable ? 'pointer' : 'auto',
              }}
            />
          ) : null}
          {leading === 'icon' && leadingIcon ? (
            <leadingIcon.Icon
              size={'24'}
              color={leadingIcon.color || colors.neutral1000}
            />
          ) : null}
          {leading === 'indicator' ? (
            <Indicator
              size={'8'}
              color={'blue'}
              type={'status'}
              containerStyle={{alignSelf: 'flex-start'}}
            />
          ) : null}
          {leading === 'buttonIcon' && leadingButtonIcon ? (
            <ButtonIcon
              {...leadingButtonIcon}
              containerStyle={{
                ...leadingButtonIcon?.containerStyle,
                cursor:
                  leadingButtonIcon?.onClick || shouldBeFullyClickable
                    ? 'pointer'
                    : 'auto',
                alignSelf: 'flex-start',
              }}
            />
          ) : null}
          {title?.length || content?.length || extraInfo?.length ? (
            <ListMetaContainer
              condition={condition}
              align={'left'}
              color={mainTextColor ? mainTextColor : 'black'}
              actionItem={mainActionItem}
              infoColor={mainTextInfoColor || mainTextColor || 'black'}
              title={title}
              content={content}
              extraInfo={extraInfo}
              size={'medium'}
              containerStyle={{alignSelf: 'center'}}
            />
          ) : null}
        </div>
        {trailing === 'checkbox' && checkbox ? (
          <Checkbox
            {...checkbox}
            onClick={e => {
              e.stopPropagation();
              (checkbox?.onClick && checkbox?.onClick()) ||
                (onClick && onClick());
            }}
            containerStyle={{alignSelf: 'center', marginLeft: 'auto'}}
          />
        ) : null}
        {trailing === 'iconGroup' && iconGroup ? (
          <IconGroup
            {...iconGroup}
            containerStyle={{alignSelf: 'center', marginLeft: 'auto'}}
          />
        ) : null}
        {trailing === 'radioButton' && radioButton ? (
          <RadioButton
            {...radioButton}
            onClick={e => {
              e.stopPropagation();
              (radioButton.onClick && radioButton.onClick()) ||
                (onClick && onClick());
            }}
            containerStyle={{alignSelf: 'center', marginLeft: 'auto'}}
          />
        ) : null}
        {trailing === 'switch' && switchOpt ? (
          <Switch
            {...switchOpt}
            onChange={switchOpt.onChange || onClick}
            containerStyle={{alignSelf: 'center', marginLeft: 'auto'}}
          />
        ) : null}
        {trailing === 'icon' && trailingIcon ? (
          <trailingIcon.Icon
            size={'24'}
            color={trailingIcon?.color || colors.neutral1000}
          />
        ) : null}
        {trailing === 'segmentedButton' && segmentedButton ? (
          <SegmentedButton
            {...segmentedButton}
            containerStyle={{
              alignSelf: 'center',
              marginLeft: 'auto',
              flex: 1,
              width: 'auto',
            }}
          />
        ) : null}
        {trailing === 'value' && trailingValue ? (
          <ListMetaContainer
            condition={'all'}
            title={trailingValue}
            containerStyle={{alignSelf: 'center', marginLeft: 'auto'}}
          />
        ) : null}
        {trailing === 'action' && actionItem ? (
          <ActionItems
            {...actionItem}
            onClick={e => {
              e.stopPropagation();
              actionItem.onClick ? actionItem.onClick() : onClick && onClick();
            }}
            containerStyle={{alignSelf: 'center', marginLeft: 'auto'}}
          />
        ) : null}
        {trailing === 'notifications' && notification ? (
          <div
            id={notification?.id}
            className={notification?.className}
            style={{
              ...generalStyles.notificationContainer,
              marginLeft: 'auto',
            }}
            onClick={e => {
              e.stopPropagation();
              notification.onClick && notification.onClick();
            }}>
            <p
              style={{
                ...fontStyles['bodyRegularXS'],
                lineHeight: fontStyles['bodyRegularXS']?.lineHeight + 'px',
                margin: 0,
                color:
                  state === 'active' ? colors.neutral100 : colors.neutral800,
              }}>
              {notification.text}
            </p>
            <div
              style={{
                transform: 'rotate(-90deg)',
                width: 16,
                display: 'flex',
                justifyContent: 'center',
                marginLeft: 'auto',
              }}>
              <MoreVerticalIcon
                size={'16'}
                color={
                  state === 'active' ? colors.neutral100 : colors.neutral800
                }
              />
            </div>
          </div>
        ) : null}
        {trailing === 'button' && button ? (
          <Button
            {...button}
            containerStyle={{alignSelf: 'center', marginLeft: 'auto'}}
          />
        ) : null}
        {trailing === 'chip' && chip ? (
          <Chip
            {...chip}
            containerStyle={{alignSelf: 'center', marginLeft: 'auto'}}
          />
        ) : null}
      </div>

      {helperText ? (
        <p
          style={{
            color: colors.neutral600,
            ...fontStyles['bodyRegularCap'],
            lineHeight: fontStyles['bodyRegularCap'].lineHeight + 'px',
            margin: 0,
          }}>
          {helperText}
        </p>
      ) : null}
    </div>
  );
};

export default ListItem;
